import {memo, useMemo, useState} from 'react'
import {View} from 'react-native'
import {AtUri} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import type React from 'react'

import {makeProfileLink} from '#/lib/routes/links'
import {type NavigationProp} from '#/lib/routes/types'
import {shareText, shareUrl} from '#/lib/sharing'
import {
  renderShareCardToPngUri,
  TellusInstagramShareCardRenderer,
} from '#/lib/sharing/renderShareCard'
import {sharePostToInstagramStory} from '#/lib/sharing/instagramStories'
import {toShareUrl} from '#/lib/strings/url-helpers'
import {logger} from '#/logger'
import {isWeb} from '#/platform/detection'
import {useAgeAssurance} from '#/state/ageAssurance/useAgeAssurance'
import {useProfileShadow} from '#/state/cache/profile-shadow'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints} from '#/alf'
import {useDialogControl} from '#/components/Dialog'
import {EmbedDialog} from '#/components/dialogs/Embed'
import {SendViaChatDialog} from '#/components/dms/dialogs/ShareViaChatDialog'
import {Camera_Stroke2_Corner0_Rounded as CameraIcon} from '#/components/icons/Camera'
import {ChainLink_Stroke2_Corner0_Rounded as ChainLinkIcon} from '#/components/icons/ChainLink'
import {Clipboard_Stroke2_Corner2_Rounded as ClipboardIcon} from '#/components/icons/Clipboard'
import {CodeBrackets_Stroke2_Corner0_Rounded as CodeBracketsIcon} from '#/components/icons/CodeBrackets'
import {PaperPlane_Stroke2_Corner0_Rounded as Send} from '#/components/icons/PaperPlane'
import {Loader} from '#/components/Loader'
import * as Menu from '#/components/Menu'
import * as Prompt from '#/components/Prompt'
import {useDevMode} from '#/storage/hooks/dev-mode'
import {type ShareMenuItemsProps} from './ShareMenuItems.types'

let ShareMenuItems = ({
  post,
  record,
  timestamp,
  onShare: onShareProp,
}: ShareMenuItemsProps): React.ReactNode => {
  const {hasSession} = useSession()
  const {gtMobile} = useBreakpoints()
  const {_} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const embedPostControl = useDialogControl()
  const sendViaChatControl = useDialogControl()
  const instagramFallbackControl = Prompt.usePromptControl()
  const instagramLoadingControl = Prompt.usePromptControl()
  const [devModeEnabled] = useDevMode()
  const {isAgeRestricted} = useAgeAssurance()
  const [isSharingInstagram, setIsSharingInstagram] = useState(false)
  const [shareCard, setShareCard] = useState<{
    uri: string
    mime: 'image/png'
  } | null>(null)
  const [instagramFallbackReason, setInstagramFallbackReason] = useState<
    string | undefined
  >(undefined)

  const postUri = post.uri
  const postCid = post.cid
  const postAuthor = useProfileShadow(post.author)

  const href = useMemo(() => {
    const urip = new AtUri(postUri)
    return makeProfileLink(postAuthor, 'post', urip.rkey)
  }, [postUri, postAuthor])

  const hideInPWI = useMemo(() => {
    return !!postAuthor.labels?.find(
      label => label.val === '!no-unauthenticated',
    )
  }, [postAuthor])

  const onCopyLink = () => {
    logger.metric('share:press:copyLink', {}, {statsig: true})
    const url = toShareUrl(href)
    shareUrl(url)
    onShareProp()
  }

  const ensureShareCard = async () => {
    if (shareCard) return shareCard
    const rendered = await renderShareCardToPngUri(post)
    setShareCard(rendered)
    return rendered
  }

  const onDownloadShareImage = async () => {
    const rendered = await ensureShareCard()
    const anchor = document.createElement('a')
    anchor.href = rendered.uri
    anchor.download = 'tellus-instagram-story.png'
    anchor.click()
  }

  const onShareInstagramStory = async () => {
    if (isSharingInstagram) return
    setIsSharingInstagram(true)
    instagramLoadingControl.open()
    try {
      const rendered = await renderShareCardToPngUri(post)
      setShareCard(rendered)
      const result = await sharePostToInstagramStory(post)
      if (!result.ok) {
        setInstagramFallbackReason(result.reason)
        instagramLoadingControl.close()
        instagramFallbackControl.open()
      }
    } catch (error: any) {
      logger.error('Failed to share Instagram Story', {message: error})
      setInstagramFallbackReason('error')
      instagramLoadingControl.close()
      instagramFallbackControl.open()
    } finally {
      instagramLoadingControl.close()
      setIsSharingInstagram(false)
      onShareProp()
    }
  }

  const onSelectChatToShareTo = (conversation: string) => {
    logger.metric('share:press:dmSelected', {}, {statsig: true})
    navigation.navigate('MessagesConversation', {
      conversation,
      embed: postUri,
    })
  }

  const canEmbed = isWeb && gtMobile && !hideInPWI

  const onShareATURI = () => {
    shareText(postUri)
  }

  const onShareAuthorDID = () => {
    shareText(postAuthor.did)
  }

  const instagramFallbackTitle = useMemo(() => {
    if (instagramFallbackReason === 'web') {
      return _(msg`Instagram Stories unavailable`)
    }
    if (instagramFallbackReason === 'not_installed') {
      return _(msg`Instagram not installed`)
    }
    return _(msg`Unable to share to Instagram`)
  }, [instagramFallbackReason, _])

  const instagramFallbackDescription = useMemo(() => {
    if (instagramFallbackReason === 'web') {
      return _(
        msg`Instagram Stories sharing isn't available on web. You can still copy the link or download the image.`,
      )
    }
    if (instagramFallbackReason === 'not_installed') {
      return _(
        msg`Instagram is not installed on this device. You can still copy the link or download the image.`,
      )
    }
    return _(
      msg`We couldn't open Instagram Stories. You can still copy the link or download the image.`,
    )
  }, [instagramFallbackReason, _])

  const copyLinkItem = (
    <Menu.Item
      testID="postDropdownShareBtn"
      label={_(msg`Copy link to post`)}
      onPress={onCopyLink}>
      <Menu.ItemText>
        <Trans>Copy link to post</Trans>
      </Menu.ItemText>
      <Menu.ItemIcon icon={ChainLinkIcon} position="right" />
    </Menu.Item>
  )

  return (
    <>
      <Menu.Outer>
        {!hideInPWI && copyLinkItem}

        {!hideInPWI && (
          <Menu.Item
            testID="postDropdownShareInstagramStoryBtn"
            label={_(msg`Share to Instagram Story`)}
            onPress={onShareInstagramStory}
            disabled={isSharingInstagram}>
            <Menu.ItemText>
              <Trans>Share to Instagram Story</Trans>
            </Menu.ItemText>
            <Menu.ItemIcon
              icon={isSharingInstagram ? Loader : CameraIcon}
              position="right"
            />
          </Menu.Item>
        )}

        {hasSession && !isAgeRestricted && (
          <Menu.Item
            testID="postDropdownSendViaDMBtn"
            label={_(msg`Send via direct message`)}
            onPress={() => {
              logger.metric('share:press:openDmSearch', {}, {statsig: true})
              sendViaChatControl.open()
            }}>
            <Menu.ItemText>
              <Trans>Send via direct message</Trans>
            </Menu.ItemText>
            <Menu.ItemIcon icon={Send} position="right" />
          </Menu.Item>
        )}

        {canEmbed && (
          <Menu.Item
            testID="postDropdownEmbedBtn"
            label={_(msg`Embed post`)}
            onPress={() => {
              logger.metric('share:press:embed', {}, {statsig: true})
              embedPostControl.open()
            }}>
            <Menu.ItemText>{_(msg`Embed post`)}</Menu.ItemText>
            <Menu.ItemIcon icon={CodeBracketsIcon} position="right" />
          </Menu.Item>
        )}

        {hideInPWI && (
          <>
            {hasSession && <Menu.Divider />}
            {copyLinkItem}
            <Menu.LabelText style={{maxWidth: 220}}>
              <Trans>Note: This post is only visible to logged-in users.</Trans>
            </Menu.LabelText>
          </>
        )}

        {devModeEnabled && (
          <>
            <Menu.Divider />
            <Menu.Item
              testID="postAtUriShareBtn"
              label={_(msg`Copy post at:// URI`)}
              onPress={onShareATURI}>
              <Menu.ItemText>
                <Trans>Copy post at:// URI</Trans>
              </Menu.ItemText>
              <Menu.ItemIcon icon={ClipboardIcon} position="right" />
            </Menu.Item>
            <Menu.Item
              testID="postAuthorDIDShareBtn"
              label={_(msg`Copy author DID`)}
              onPress={onShareAuthorDID}>
              <Menu.ItemText>
                <Trans>Copy author DID</Trans>
              </Menu.ItemText>
              <Menu.ItemIcon icon={ClipboardIcon} position="right" />
            </Menu.Item>
          </>
        )}
      </Menu.Outer>

      {canEmbed && (
        <EmbedDialog
          control={embedPostControl}
          postCid={postCid}
          postUri={postUri}
          record={record}
          postAuthor={postAuthor}
          timestamp={timestamp}
        />
      )}

      <SendViaChatDialog
        control={sendViaChatControl}
        onSelectChat={onSelectChatToShareTo}
      />

      <Prompt.Outer control={instagramLoadingControl}>
        <Prompt.TitleText>
          <Trans>Preparing Instagram Story</Trans>
        </Prompt.TitleText>
        <View style={[a.align_center, a.justify_center, a.py_lg]}>
          <Loader size="xl" />
        </View>
      </Prompt.Outer>

      <Prompt.Outer control={instagramFallbackControl}>
        <Prompt.TitleText>{instagramFallbackTitle}</Prompt.TitleText>
        <Prompt.DescriptionText>
          {instagramFallbackDescription}
        </Prompt.DescriptionText>
        <Prompt.Actions>
          <Prompt.Action
            cta={_(msg`Copy link`)}
            onPress={async () => {
              onCopyLink()
            }}
          />
          <Prompt.Action
            cta={_(msg`Download image`)}
            onPress={async () => {
              await onDownloadShareImage()
            }}
          />
          <Prompt.Cancel cta={_(msg`Cancel`)} />
        </Prompt.Actions>
      </Prompt.Outer>

      <TellusInstagramShareCardRenderer />
    </>
  )
}
ShareMenuItems = memo(ShareMenuItems)
export {ShareMenuItems}
